import React, { useCallback, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CAMERA_ROLES, openRoleCamera } from '../Utils/cameraSettings';
import { NAVY, GREEN } from './raffleTokens';
import { Overlay } from './RafflePieces';

// Long edge of the saved photo. Enough to recognise a face on the receipt and
// in an audit, small enough to upload quickly on the floor's Wi-Fi.
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;

/**
 * R34 — a live capture of the person collecting the money, taken now. The
 * photo on the customer's file is never a substitute.
 */
export default function CameraSheet({
  customerName,
  locationId,
  onCapture,
  onClose,
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState('user');
  const [shot, setShot] = useState(null); // { blob, url }
  const [error, setError] = useState('');

  const stop = useCallback(() => {
    const s = streamRef.current;
    if (s) s.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const start = useCallback(async () => {
    stop();
    setError('');
    try {
      const stream = await openRoleCamera(
        CAMERA_ROLES.GENERAL,
        {
          audio: false,
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        },
        locationId
      );
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      setError(
        err && err.name === 'NotAllowedError'
          ? 'Camera permission was denied. Allow the camera for this site and try again.'
          : 'The camera could not be opened.'
      );
    }
  }, [facing, locationId, stop]);

  useEffect(() => {
    if (!shot) start();
    return stop;
  }, [shot, start, stop]);

  useEffect(
    () => () => {
      if (shot?.url) URL.revokeObjectURL(shot.url);
    },
    [shot]
  );

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const scale = Math.min(
      1,
      MAX_EDGE / Math.max(video.videoWidth, video.videoHeight)
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        stop();
        setShot({ blob, url: URL.createObjectURL(blob) });
      },
      'image/jpeg',
      JPEG_QUALITY
    );
  };

  const first = (customerName || '').split(' ')[0] || 'the customer';

  return (
    <Overlay>
      <div className="rf-camera">
        <div className="rf-camera-top">
          <button
            type="button"
            className="rf-icon-btn"
            onClick={onClose}
            aria-label="Close camera"
          >
            <CloseIcon style={{ color: '#fff', fontSize: 26 }} />
          </button>
          <span className="rf-camera-title">Photo of {first}</span>
          {!shot ? (
            <button
              type="button"
              className="rf-icon-btn"
              onClick={() =>
                setFacing((f) => (f === 'user' ? 'environment' : 'user'))
              }
              aria-label="Switch camera"
            >
              <CameraswitchIcon style={{ color: '#fff', fontSize: 24 }} />
            </button>
          ) : (
            <span style={{ width: 40 }} />
          )}
        </div>

        <div className="rf-camera-body">
          <div className="rf-camera-frame">
            {!shot ? (
              <>
                <video
                  ref={videoRef}
                  className="rf-camera-video"
                  autoPlay
                  playsInline
                  muted
                />
                <div className="rf-camera-guide" />
                <span className="rf-camera-hint">
                  {error || "Center the customer's face"}
                </span>
              </>
            ) : (
              <img src={shot.url} alt="Captured" className="rf-camera-video" />
            )}
          </div>
          {shot && (
            <div
              className="rf-row rf-gap-8 rf-center"
              style={{ marginTop: 16 }}
            >
              <CheckCircleIcon style={{ color: '#43e08d', fontSize: 18 }} />
              <span className="rf-camera-ok">Photo captured</span>
            </div>
          )}
        </div>

        <div className="rf-camera-actions">
          {!shot ? (
            <button
              type="button"
              className="rf-shutter"
              onClick={capture}
              disabled={!!error}
              aria-label="Take photo"
            >
              <PhotoCameraIcon style={{ color: NAVY, fontSize: 30 }} />
            </button>
          ) : (
            <div className="rf-row rf-gap-12">
              <button
                type="button"
                className="rf-camera-btn"
                onClick={() => setShot(null)}
              >
                Retake
              </button>
              <button
                type="button"
                className="rf-camera-btn"
                style={{
                  background: GREEN,
                  borderColor: GREEN,
                  fontWeight: 600,
                }}
                onClick={() => onCapture(shot.blob)}
              >
                Use this photo
              </button>
            </div>
          )}
        </div>
      </div>
    </Overlay>
  );
}
